// Google Auth Service for PlexiSystem
// Location: /src/modules/googleAds/services/googleAuthService.ts

import axios from 'axios';

// API configuration
const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:3001/api'
  : 'https://api.plexisystem.pl/api';

// Google OAuth2 configuration
const GOOGLE_CLIENT_ID = '718148264793-652ttnfun23h3vbt84u4e5tq04cp7okk.apps.googleusercontent.com';
const GOOGLE_REDIRECT_URI = window.location.hostname === 'localhost'
  ? 'http://localhost:5173/google-ads/callback'
  : 'https://app.plexisystem.pl/google-ads/callback';
  // UWAGA: Upewnij się, że te URI są dodane w Google Cloud Console!

// Scopes for Google Ads API
const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/adwords',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile'
].join(' ');

interface GoogleUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  googleId: string;
}

interface AuthSession {
  id: string;
  token: string;
  expiresAt: string;
  user: GoogleUser;
}

class GoogleAuthService {
  private currentUser: GoogleUser | null = null;
  private authToken: string | null = null;
  private sessionId: string | null = null;

  constructor() {
    this.loadStoredAuth();
    this.initGoogleScript();
  }

  // Initialize Google Sign-In script
  private async initGoogleScript() {
    // Check if script already exists
    if (document.getElementById('google-signin-script')) {
      return;
    }

    // Create and append Google Sign-In script
    const script = document.createElement('script');
    script.id = 'google-signin-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    // Wait for script to load
    return new Promise((resolve) => {
      script.onload = () => {
        console.log('🔐 Google Sign-In script loaded');
        resolve(true);
      };
    });
  }

  // Load stored authentication from localStorage
  private loadStoredAuth() {
    try {
      const storedAuth = localStorage.getItem('googleAdsAuth');
      if (storedAuth) {
        const auth = JSON.parse(storedAuth);
        const expiresAt = new Date(auth.expiresAt);
        
        // Check if token is still valid
        if (expiresAt > new Date()) {
          this.currentUser = auth.user;
          this.authToken = auth.token;
          this.sessionId = auth.sessionId;
          console.log('✅ Loaded stored auth for:', auth.user.email);
        } else {
          // Token expired, clear storage
          this.clearAuth();
          console.log('⏰ Stored auth expired, cleared');
        }
      }
    } catch (error) {
      console.error('Error loading stored auth:', error);
      this.clearAuth();
    }
  }

  // Save authentication to localStorage
  private saveAuth(session: AuthSession) {
    try {
      // Validate session data
      if (!session || !session.token) {
        console.error('❌ Invalid session data:', session);
        return;
      }

      // Ensure user object exists
      const user = session.user || {
        id: 'unknown',
        email: 'user@plexisystem.pl',
        name: 'User',
        googleId: 'unknown'
      };

      const authData = {
        user: user,
        token: session.token,
        sessionId: session.id,
        expiresAt: session.expiresAt
      };
      
      localStorage.setItem('googleAdsAuth', JSON.stringify(authData));
      localStorage.setItem('authToken', session.token); // For backwards compatibility
      
      this.currentUser = user;
      this.authToken = session.token;
      this.sessionId = session.id;
      
      console.log('💾 Auth saved for:', user.email || 'unknown user');
    } catch (error) {
      console.error('Error saving auth:', error);
      console.error('Session data that failed:', session);
    }
  }

  // Clear authentication
  private clearAuth() {
    localStorage.removeItem('googleAdsAuth');
    localStorage.removeItem('authToken');
    this.currentUser = null;
    this.authToken = null;
    this.sessionId = null;
  }

  // Initialize Google Sign-In with One Tap
  async initializeGoogleSignIn(buttonId: string, callback?: (user: GoogleUser) => void) {
    await this.initGoogleScript();

    // Wait for google object to be available
    if (!window.google) {
      console.error('Google Sign-In script not loaded');
      return;
    }

    // Initialize Google Sign-In
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response: any) => {
        console.log('🔐 Google Sign-In response received');
        await this.handleGoogleResponse(response, callback);
      },
      auto_select: false,
      cancel_on_tap_outside: true
    });

    // Render the sign-in button
    const buttonElement = document.getElementById(buttonId);
    if (buttonElement) {
      window.google.accounts.id.renderButton(buttonElement, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 250
      });
    }

    // Show One Tap prompt if not signed in
    if (!this.isAuthenticated()) {
      window.google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.log('One Tap not displayed:', notification.getNotDisplayedReason());
        }
      });
    }
  }

  // Handle Google Sign-In response
  private async handleGoogleResponse(response: any, callback?: (user: GoogleUser) => void) {
    try {
      console.log('Processing Google Sign-In...');
      
      // Decode JWT token to get user info
      const credential = response.credential;
      const decodedToken = this.parseJwt(credential);
      
      if (!decodedToken) {
        throw new Error('Failed to decode Google token');
      }

      const googleUser: GoogleUser = {
        id: decodedToken.sub,
        email: decodedToken.email,
        name: decodedToken.name,
        picture: decodedToken.picture,
        googleId: decodedToken.sub
      };

      console.log('👤 Google user:', googleUser.email);

      // Send to backend to create session
      const sessionResponse = await axios.post(`${API_BASE_URL}/sessions/login`, {
        email: googleUser.email,
        name: googleUser.name,
        googleId: googleUser.googleId,
        token: credential
      });

      if (sessionResponse.data.success) {
        const session: AuthSession = {
          id: sessionResponse.data.session.id,
          token: sessionResponse.data.session.token,
          expiresAt: sessionResponse.data.session.expiresAt,
          user: sessionResponse.data.user
        };

        // Save authentication
        this.saveAuth(session);

        console.log('✅ Authentication successful!');
        
        // Call callback if provided
        if (callback) {
          callback(googleUser);
        }

        // Reload page to apply auth
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        throw new Error('Backend authentication failed');
      }
    } catch (error) {
      console.error('❌ Authentication error:', error);
      this.clearAuth();
      throw error;
    }
  }

  // Parse JWT token
  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return null;
    }
  }

  // OAuth2 flow methods (alternative to One Tap)
  initiateOAuth2Flow() {
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    
    authUrl.searchParams.append('client_id', GOOGLE_CLIENT_ID);
    authUrl.searchParams.append('redirect_uri', GOOGLE_REDIRECT_URI);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('scope', GOOGLE_SCOPES);
    authUrl.searchParams.append('access_type', 'offline');
    authUrl.searchParams.append('prompt', 'consent');
    authUrl.searchParams.append('state', this.generateState());

    console.log('🔗 Redirecting to Google OAuth2...');
    window.location.href = authUrl.toString();
  }

  // Handle OAuth2 callback
  async handleOAuth2Callback(code: string, state: string) {
    try {
      console.log('Processing OAuth2 callback...');
      console.log('📍 Exchanging code for tokens with backend...');
      
      // Exchange code for tokens
      const response = await axios.post(`${API_BASE_URL}/sessions/oauth2/exchange`, {
        code,
        state,
        redirect_uri: GOOGLE_REDIRECT_URI
      });
      
      console.log('📦 Backend response:', response.data);
      
      // Check if response is JSON
      const contentType = response.headers['content-type'];
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Non-JSON response:', response.data);
        throw new Error('Server returned non-JSON response. Backend may be down.');
      }

      // Handle different response structures
      if (response.data.success) {
        // Extract user data from response
        const userData = response.data.user || response.data.session?.user || {
          id: response.data.userId || 'unknown',
          email: response.data.email || 'unknown@plexisystem.pl',
          name: response.data.name || 'User',
          googleId: response.data.googleId || 'unknown'
        };

        // Create session object
        const session: AuthSession = {
          id: response.data.session?.id || response.data.sessionId || Date.now().toString(),
          token: response.data.session?.token || response.data.token || response.data.access_token,
          expiresAt: response.data.session?.expiresAt || response.data.expiresAt || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          user: userData
        };

        console.log('✅ Session created:', { email: userData.email, sessionId: session.id });
        
        this.saveAuth(session);
        return userData;
      } else if (response.data.access_token || response.data.token) {
        // Alternative response structure (direct token response)
        console.log('⚠️ Alternative response structure detected');
        
        // Try to get user info from token
        const token = response.data.access_token || response.data.token;
        const userData = {
          id: response.data.id || 'google-user',
          email: response.data.email || 'user@plexisystem.pl',
          name: response.data.name || 'Google User',
          googleId: response.data.googleId || response.data.id || 'unknown'
        };

        const session: AuthSession = {
          id: Date.now().toString(),
          token: token,
          expiresAt: new Date(Date.now() + (response.data.expires_in || 3600) * 1000).toISOString(),
          user: userData
        };

        console.log('✅ Session created from token response');
        this.saveAuth(session);
        return userData;
      } else {
        console.error('❌ Unexpected response structure:', response.data);
        throw new Error('OAuth2 exchange failed - unexpected response structure');
      }
    } catch (error: any) {
      console.error('OAuth2 callback error:', error);
      console.error('Error details:', error.response?.data);
      throw error;
    }
  }

  // Generate random state for OAuth2
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  // Sign out
  async signOut() {
    try {
      // Notify backend
      if (this.sessionId) {
        await axios.post(`${API_BASE_URL}/sessions/logout`, {
          sessionId: this.sessionId
        });
      }

      // Clear local auth
      this.clearAuth();

      // Revoke Google access if available
      if (window.google && window.google.accounts) {
        window.google.accounts.id.disableAutoSelect();
      }

      console.log('👋 Signed out successfully');
      
      // Reload page
      window.location.href = '/google-ads';
    } catch (error) {
      console.error('Sign out error:', error);
      // Clear auth anyway
      this.clearAuth();
      window.location.href = '/google-ads';
    }
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    if (!this.authToken) return false;
    
    // Check if we have stored auth
    const storedAuth = localStorage.getItem('googleAdsAuth');
    if (!storedAuth) return false;
    
    try {
      const auth = JSON.parse(storedAuth);
      const expiresAt = new Date(auth.expiresAt);
      return expiresAt > new Date();
    } catch {
      return false;
    }
  }

  // Get current user
  getCurrentUser(): GoogleUser | null {
    return this.currentUser;
  }

  // Get auth token
  getAuthToken(): string | null {
    return this.authToken;
  }

  // Get session ID
  getSessionId(): string | null {
    return this.sessionId;
  }

  // Validate current session with backend
  async validateSession(): Promise<boolean> {
    if (!this.authToken) return false;

    try {
      const response = await axios.get(`${API_BASE_URL}/sessions/validate`, {
        headers: {
          Authorization: `Bearer ${this.authToken}`
        }
      });

      return response.data.valid === true;
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  }
}

// Create singleton instance
export const googleAuthService = new GoogleAuthService();

// Export types
export type { GoogleUser, AuthSession };

// Add TypeScript declarations for Google Sign-In
declare global {
  interface Window {
    google: any;
  }
}
